import { CheckIcon } from '@heroicons/react/solid';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useState } from 'react';
import { Price, ProductWithPrice } from 'types';

import { STARTER_PROMO } from '@/constants/common';
import { postData } from '@/utils/helpers';
import httpClient from '@/utils/httpClient';
import { getStripe } from '@/utils/stripe-client';
import { useUser } from '@/utils/useUser';

import Modal from '../atoms/Modal';
import Button from '../buttons/Button';

interface UpgradeModalState {
  price: Price;
  product: ProductWithPrice;
  amountDue: number | null;
  currency: string;
  prorationDate: number;
  planName: string | null;
  interval: 'month' | 'year';
  addonQuantity: number;
}

type UpgradeStatus = 'idle' | 'processing' | 'success' | 'failed';

export const isStarterMonthlyPromo = (
  product: ProductWithPrice,
  price: Price,
) =>
  product.id === STARTER_PROMO.productId &&
  price.interval === STARTER_PROMO.billingInterval;

/**
 * Buying or changing a plan from a pricing card. Visitors go to sign up with
 * the price kept, Free users to Stripe Checkout, and paying users through a
 * plan change (an in-app proration preview when their subscription has an
 * add-on, the Stripe portal otherwise), so a paid user never ends up with a
 * second subscription. Render `planChangeModal` once next to the cards.
 */
export function usePlanCheckout({
  isUpgrade = false,
  withTrial = false,
}: {
  isUpgrade?: boolean;
  // Ask the checkout for a 7-day free trial. The server only grants it to
  // users with no prior subscription history and silently falls back to a
  // normal checkout otherwise, so showing this optimistically is safe.
  withTrial?: boolean;
} = {}) {
  const router = useRouter();
  const { t } = useLocale('pricing');
  const { user, subscription, isFreePlan } = useUser();
  const [priceIdLoading, setPriceIdLoading] = useState<string>();
  const [upgradeModal, setUpgradeModal] = useState<UpgradeModalState | null>(
    null,
  );
  const [upgradeStatus, setUpgradeStatus] = useState<UpgradeStatus>('idle');
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgradePaymentFailed, setUpgradePaymentFailed] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleCheckout = async (price: Price, product: ProductWithPrice) => {
    setPriceIdLoading(price.id);

    const promotionCode = isStarterMonthlyPromo(product, price)
      ? STARTER_PROMO.promotionCodeId
      : undefined;

    if (!user) {
      const params = new URLSearchParams({ price: price.id });
      if (promotionCode) params.set('promotionCode', promotionCode);
      return router.push(`/login?${params.toString()}#auth-sign-up`);
    }

    const hasNonStripeSubscription =
      subscription?.id?.startsWith('sub_activated_') ||
      subscription?.id?.startsWith('sub_b2b_auto_') ||
      false;

    if (!isFreePlan) {
      if (hasNonStripeSubscription) {
        try {
          const { sessionId } = await postData({
            url: '/api/create-checkout-session',
            data: { price: price.id, isUpgrade: true, promotionCode },
            token: '',
          });

          const stripe = await getStripe();
          stripe?.redirectToCheckout({ sessionId });
        } catch (error) {
          console.log('error', error);
          if (error) return alert((error as Error).message);
        } finally {
          setPriceIdLoading(undefined);
        }
      } else {
        // Subscriptions with an add-on item (e.g. Always-On Hosting) can't be
        // updated in the Stripe portal (it hides "Update plan" for
        // multi-product subscriptions), so ask the server first: it returns an
        // in-app proration preview for those, or { usePortal: true } for
        // plain single-product subscriptions
        let upgradePreview: UpgradeModalState | null = null;
        try {
          const { data } = await httpClient.post(
            '/api/upgrade-subscription',
            { price: price.id, preview: true },
            { skipErrorToast: true },
          );
          if (data?.preview) {
            upgradePreview = {
              price,
              product,
              ...data.preview,
            };
          }
        } catch (error) {
          // Preview unavailable — fall through to the portal flow below
          console.log('upgrade preview error', error);
        }

        if (upgradePreview) {
          setUpgradeStatus('idle');
          setUpgradeError(null);
          setUpgradePaymentFailed(false);
          setUpgradeModal(upgradePreview);
          setPriceIdLoading(undefined);
          return;
        }

        try {
          const { url } = await postData({
            url: '/api/create-portal-link',
            token: '',
            data: { price: '', flow_data_type: 'subscription_update' },
          });
          window.location.assign(url);
        } catch (error) {
          console.log('error', error);
          if (error) return alert((error as Error).message);
        }
      }
    } else {
      try {
        const { sessionId } = await postData({
          url: '/api/create-checkout-session',
          data: { price: price.id, isUpgrade, promotionCode, withTrial },
          token: '',
        });

        const stripe = await getStripe();
        stripe?.redirectToCheckout({ sessionId });
      } catch (error) {
        return alert((error as Error)?.message);
      } finally {
        setPriceIdLoading(undefined);
      }
    }
  };

  const formatAmount = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'usd',
    }).format(amount / 100);

  const handleConfirmUpgrade = async () => {
    if (!upgradeModal) return;
    setUpgradeStatus('processing');
    setUpgradeError(null);
    setUpgradePaymentFailed(false);
    try {
      await httpClient.post(
        '/api/upgrade-subscription',
        {
          price: upgradeModal.price.id,
          prorationDate: upgradeModal.prorationDate,
        },
        { skipErrorToast: true },
      );
      setUpgradeStatus('success');
      // Reload so useUser/SSR pick up the new plan everywhere
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      const data = error?.response?.data;
      setUpgradeStatus('failed');
      setUpgradePaymentFailed(Boolean(data?.paymentFailed));
      setUpgradeError(
        typeof data?.error === 'string'
          ? data.error
          : t('We could not process your payment.'),
      );
    }
  };

  // Opens the plain Stripe portal (payment methods live there) in a new tab
  // so the user can fix their card and come back to retry the upgrade
  const handleOpenPortalForPayment = async () => {
    setIsOpeningPortal(true);
    try {
      const { url } = await postData({
        url: '/api/create-portal-link',
        token: '',
      });
      window.open(url, '_blank');
    } catch (error) {
      console.log('error', error);
    }
    setIsOpeningPortal(false);
  };

  const closeUpgradeModal = () => {
    if (upgradeStatus === 'processing' || upgradeStatus === 'success') return;
    setUpgradeModal(null);
  };

  const planChangeModal = (
    <Modal
      showModal={Boolean(upgradeModal)}
      onClose={closeUpgradeModal}
      className='sm:max-w-lg'
      doNotCloseOnOverlayClick={
        upgradeStatus === 'processing' || upgradeStatus === 'success'
      }
    >
      {upgradeModal && (
        <div className='p-6'>
          {upgradeStatus === 'success' ? (
            <div className='py-6 text-center'>
              <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40'>
                <CheckIcon className='h-7 w-7 text-green-600 dark:text-green-400' />
              </div>
              <h3 className='mt-4 text-lg font-medium text-gray-900 dark:text-slate-200'>
                {t('Your plan has been updated!')}
              </h3>
              <p className='mt-2 text-sm text-gray-500 dark:text-slate-400'>
                {t('Refreshing your account...')}
              </p>
            </div>
          ) : (
            <>
              <h3 className='mb-4 text-lg font-medium text-gray-900 dark:text-slate-200'>
                {t('Confirm your plan change')}
              </h3>
              <div className='space-y-4 text-sm text-gray-600 dark:text-slate-400'>
                <div className='rounded-md bg-gray-50 p-4 dark:bg-slate-700/50'>
                  <p className='text-base font-semibold text-gray-900 dark:text-slate-200'>
                    {upgradeModal.planName ||
                      (upgradeModal.product.name as string)}
                    {' — '}
                    {t(
                      upgradeModal.interval === 'year'
                        ? 'Yearly billing'
                        : 'Monthly billing',
                    )}
                  </p>
                  {upgradeModal.amountDue !== null &&
                  upgradeModal.amountDue > 0 ? (
                    <>
                      <p className='mt-2 text-gray-900 dark:text-slate-200'>
                        {t('Due today:')}{' '}
                        <b>
                          {formatAmount(
                            upgradeModal.amountDue,
                            upgradeModal.currency,
                          )}
                        </b>
                      </p>
                      <p className='mt-1 text-xs'>
                        {t(
                          'Prorated — you only pay the difference for the unused part of your current billing period.',
                        )}
                      </p>
                    </>
                  ) : upgradeModal.amountDue !== null ? (
                    <p className='mt-2'>
                      {t(
                        'No payment is due today — your unused balance covers this change.',
                      )}
                    </p>
                  ) : (
                    <p className='mt-2'>
                      {t(
                        'You will only be charged the prorated difference for the rest of your billing period.',
                      )}
                    </p>
                  )}
                </div>
                <p>
                  ⚡{' '}
                  {t('Your Always-On Hosting add-on stays active') +
                    (upgradeModal.addonQuantity > 1
                      ? ` (${upgradeModal.addonQuantity} apps)`
                      : '') +
                    '.'}
                </p>
                {upgradeStatus === 'failed' && (
                  <div className='rounded-md bg-red-50 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-300'>
                    <p className='font-medium'>{upgradeError}</p>
                    {upgradePaymentFailed && (
                      <p className='mt-2 text-xs'>
                        {t(
                          'Your plan was not changed. Update your payment method in the Stripe billing portal, then try again.',
                        )}
                      </p>
                    )}
                    {upgradePaymentFailed && (
                      <Button
                        className='mt-3'
                        variant='outline'
                        isLoading={isOpeningPortal}
                        onClick={handleOpenPortalForPayment}
                      >
                        {t('Update payment method')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className='mt-6 flex justify-end space-x-3'>
                <Button
                  variant='light'
                  disabled={upgradeStatus === 'processing'}
                  onClick={closeUpgradeModal}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  variant='primary'
                  isLoading={upgradeStatus === 'processing'}
                  disabled={upgradeStatus === 'processing'}
                  onClick={handleConfirmUpgrade}
                >
                  {upgradeStatus === 'failed'
                    ? t('Try again')
                    : t('Confirm change')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );

  return { handleCheckout, priceIdLoading, planChangeModal };
}
